/// <amd-module name="Build/Admin/PoolsList/Components/PoolsHub" />
import * as React from "react";

import { caseInsensitiveContains } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IVssPageService, IPageData } from "VSS/Platform/Page";

import { Card } from "VSSUI/Card";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { DropdownFilterBarItem } from "VSSUI/Dropdown";
import { FilterBar } from "VSSUI/FilterBar";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem, getFilterItem } from "VSSUI/HeaderCommandBar";
import { IListSelection } from "VSSUI/List";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { DropdownMultiSelection } from "VSSUI/Utilities/DropdownSelection";
import { Filter, FILTER_CHANGE_EVENT } from "VSSUI/Utilities/Filter";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";

import { TaskAgentQueue, TaskAgentPool, TaskAgentPoolStatus, TaskAgentPoolReference } from "DistributedTask/Client/Contracts/TaskAgent";

import { ConfirmButtonType } from "Build/Admin/Common/Actions/ConfirmationDialogActions";
import { ConfirmationDialog } from "Build/Admin/Common/Components/ConfirmationDialog";
import { NotificationArea } from "Build/Admin/Common/Components/NotificationArea";
import { Assets, FilterKeys } from "Build/Admin/Common/Constants";
import { IPoolAdminLinkingService } from "Build/Admin/Common/Services/PoolAdminLinkingService";
import { IProjectDataService } from "Build/Admin/Common/Services/ProjectDataService";
import { INotification } from "Build/Admin/Common/Stores/NotificationStore";
import { isElastic } from "Build/Admin/Common/TaskAgentPoolUtils";
import { PoolsListActionCreator } from "Build/Admin/PoolsList/Actions/PoolsListActionCreator";
import { CreatePoolPanel } from "Build/Admin/PoolsList/Components/CreatePoolPanel";
import { PoolsList } from "Build/Admin/PoolsList/Components/PoolsList";
import { SecurityPanel } from "Build/Admin/PoolsList/Components/SecurityPanel";
import { StoresHub } from "Build/Admin/PoolsList/Stores/StoresHub";

import * as Resources from "Build/Admin/PoolsList/Resources";

export interface IPoolsHubProps {
    actionCreator: PoolsListActionCreator;
    storesHub: StoresHub;
}

export interface IPoolsHubState {
    agentQueues: TaskAgentQueue[];
    agentPools: TaskAgentPool[];
    filterApplied: boolean;
    agentPoolStatuses: { [key: string]: TaskAgentPoolStatus };
    notifications: INotification[];
    showFilterBar: boolean;
}

const filterBarPoolTypes: string[] = [
    Resources.PrivatePoolType,
    Resources.HostedPoolType,
    Resources.ElasticPoolType
];

export class PoolsHub extends VssComponent<IPoolsHubProps, IPoolsHubState> {
    private _onPrem: boolean;
    private _projectDataService: IProjectDataService;
    private _poolFilter: Filter;
    private _poolTypeSelection: IListSelection;

    constructor(props: IPoolsHubProps, context: IVssComponentContext) {
        super(props, context);

        this._poolFilter = new Filter();
        this._poolTypeSelection = new DropdownMultiSelection();
        this._projectDataService = this.context.pageContext.getService<IProjectDataService>("IProjectDataService");

        const vssPageService: IVssPageService = context.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData: IPageData | undefined = vssPageService.getData();

        if (vssPageData) {
            this._onPrem = !vssPageData.isHosted;
        }

        this.state = this._getStateFromStores(false);
    }

    public componentDidMount(): void {
        this.props.storesHub.agentPoolStore.addChangedListener(this._onStoreChanged);
        this.props.storesHub.agentPoolStatusStore.addChangedListener(this._onStoreChanged);
        this.props.storesHub.agentQueueStore.addChangedListener(this._onStoreChanged);
        this.props.storesHub.notificationStore.addChangedListener(this._onStoreChanged);
        this._poolFilter.subscribe(this._onStoreChanged, FILTER_CHANGE_EVENT);
    }

    public componentWillUnmount(): void {
        this.props.storesHub.agentPoolStore.removeChangedListener(this._onStoreChanged);
        this.props.storesHub.agentPoolStatusStore.removeChangedListener(this._onStoreChanged);
        this.props.storesHub.agentQueueStore.removeChangedListener(this._onStoreChanged);
        this.props.storesHub.notificationStore.removeChangedListener(this._onStoreChanged);
        this._poolFilter.unsubscribe(this._onStoreChanged, FILTER_CHANGE_EVENT);
    }

    public render(): JSX.Element {
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow">
                    <Header title={Resources.AgentPools} titleSize={TitleSize.Large} commandBarItems={this._getCommandBarItems()} />
                    <NotificationArea notifications={this.state.notifications} onNotificationDismissed={this._onNotificationDismissed} />
                    <ConditionalChildren renderChildren={this.state.showFilterBar}>
                        <div className="page-content-top page-content-left page-content-right">
                            <FilterBar filter={this._poolFilter} onDismissClicked={() => this._toggleFilterBar(true)} >
                                <KeywordFilterBarItem filterItemKey={FilterKeys.Name} />
                                <DropdownFilterBarItem
                                    filterItemKey={FilterKeys.PoolType}
                                    filter={this._poolFilter}
                                    items={filterBarPoolTypes}
                                    selection={this._poolTypeSelection}
                                    placeholder={Resources.PoolTypeFilterPlaceholder} />
                            </FilterBar>
                        </div>
                    </ConditionalChildren>
                    <div className="page-content page-content-top">
                        {
                            this._shouldRenderZeroData() &&
                            <ZeroData
                                primaryText={Resources.ZeroAgentPoolsPrimaryText}
                                secondaryText={Resources.ZeroAgentPoolsSecondaryText}
                                actionType={ZeroDataActionType.ctaButton}
                                actionText={Resources.AddPool}
                                onActionClick={() => this.props.actionCreator.showCreatePoolPanel()}
                                imagePath={getAssetLocation(this.context.pageContext, Assets.ZeroDataPng)}
                                imageAltText="" />
                        }
                        {
                            !this._shouldRenderZeroData() &&
                            <Card className="flex-grow bolt-card-no-vertical-padding" contentProps={{ contentPadding: false }}>
                                <PoolsList
                                    agentQueues={this.state.agentQueues}
                                    agentPools={this.state.agentPools}
                                    agentPoolStatuses={this.state.agentPoolStatuses}
                                    agentQueueOrPoolLinkProvider={this._getLinkToAgentQueueOrPool}
                                    onDeleteAgentQueueOrPool={this._confirmDeleteAgentQueueOrPool}
                                    onQueuePoolMaintenance={this._confirmPoolMaintenance}
                                    pageContext={this.context.pageContext} />
                            </Card>
                        }
                    </div>
                    <ConfirmationDialog confirmationDialogStore={this.props.storesHub.confirmationDialogStore} />
                    <CreatePoolPanel actionCreator={this.props.actionCreator} storesHub={this.props.storesHub} />
                    <SecurityPanel actionCreator={this.props.actionCreator} storesHub={this.props.storesHub} />
                </Page>
            </Surface>
        );
    }

    private _shouldRenderZeroData(): boolean {
        if (this._projectDataService.hasProjectData()) {
            return this.props.storesHub.agentQueueStore.state.length === 0;
        }
        else {
            return this.props.storesHub.agentPoolStore.state.length === 0;
        }
    }

    private _getCommandBarItems(): IHeaderCommandBarItem[] {
        const commandBarItems: IHeaderCommandBarItem[] = [];

        if (!this._shouldRenderZeroData()) {
            commandBarItems.push(
                getFilterItem(
                    () => {
                        this._toggleFilterBar(false);
                    },
                    this.state.filterApplied
                )
            );
        }

        commandBarItems.push(
            {
                text: Resources.Security,
                id: "security",
                onActivate: () => {
                    this.props.actionCreator.showSecurityPanel();
                }
            },
            {
                text: Resources.AddPool,
                id: "addPool",
                isPrimary: true,
                onActivate: () => {
                    this.props.actionCreator.showCreatePoolPanel();
                }
            }
        );

        return commandBarItems;
    }

    private _toggleFilterBar(resetFilter: boolean): void {
        if (resetFilter) {
            this._poolFilter.reset();
        }

        this.setState({
            showFilterBar: !this.state.showFilterBar
        });
    }

    private _onNotificationDismissed = (notification: INotification): void => {
        this.props.actionCreator.notificationActionCreator.dismissNotification(notification);
    }

    private _getLinkToAgentQueueOrPool = (queueOrPool: TaskAgentQueue | TaskAgentPool): string => {
        const linkingService = this.context.pageContext.getService<IPoolAdminLinkingService>("IPoolAdminLinkingService");

        if (this._projectDataService.hasProjectData()) {
            return linkingService.getAgentQueueUrl(queueOrPool as TaskAgentQueue);
        } else {
            return linkingService.getAgentPoolUrl(queueOrPool as TaskAgentPool);
        }
    }

    private _confirmDeleteAgentQueueOrPool = (queueOrPool: TaskAgentQueue | TaskAgentPool): void => {
        if (this._projectDataService.hasProjectData()) {
            const content: JSX.Element = (
                <FormatComponent format={this._onPrem ? Resources.ConfirmDeleteQueueMessageOnPrem : Resources.ConfirmDeleteQueueMessageHosted} >
                    <span className="fontWeightSemiBold">{queueOrPool.name}</span>
                    <br />
                    <br />
                </FormatComponent>
            );

            this.props.actionCreator.confirmationDialogActionCreator.showConfirmationDialog(
                queueOrPool as TaskAgentQueue,
                Resources.ConfirmDeleteTitle,
                content,
                ConfirmButtonType.Delete,
                this._deleteAgentQueue,
                this._dismissConfirmationDialog);
        }
        else {
            const content: JSX.Element = (
                <FormatComponent format={Resources.ConfirmDeletePoolMessage} >
                    <span className="fontWeightSemiBold">{queueOrPool.name}</span>
                    <br />
                    <br />
                </FormatComponent>
            );

            this.props.actionCreator.confirmationDialogActionCreator.showConfirmationDialog(
                queueOrPool as TaskAgentPool,
                Resources.ConfirmDeleteTitle,
                content,
                ConfirmButtonType.Delete,
                this._deleteAgentPool,
                this._dismissConfirmationDialog);
        }
    }

    private _confirmPoolMaintenance = (agentPool: TaskAgentPool): void => {
        const content: JSX.Element = (
            <FormatComponent format={Resources.ConfirmPoolMaintenanceMessage} >
                <span className="fontWeightSemiBold">{agentPool.name}</span>
            </FormatComponent>
        );

        this.props.actionCreator.confirmationDialogActionCreator.showConfirmationDialog(
            agentPool,
            Resources.ConfirmPoolMaintenanceTitle,
            content,
            ConfirmButtonType.Confirm,
            this._queueMaintenanceJob,
            this._dismissConfirmationDialog);
    }

    private _deleteAgentPool = (agentPool: TaskAgentPool): void => {
        this.props.actionCreator.deleteAgentPool(agentPool);
        this.props.actionCreator.confirmationDialogActionCreator.dismissConfirmationDialog();
    }

    private _deleteAgentQueue = (agentQueue: TaskAgentQueue): void => {
        this.props.actionCreator.deleteAgentQueue(agentQueue);
        this.props.actionCreator.confirmationDialogActionCreator.dismissConfirmationDialog();
    }

    private _queueMaintenanceJob = (agentPool: TaskAgentPool): void => {
        this.props.actionCreator.queuePoolMaintenanceJob(agentPool);
        this.props.actionCreator.confirmationDialogActionCreator.dismissConfirmationDialog();
    }

    private _dismissConfirmationDialog = (): void => {
        this.props.actionCreator.confirmationDialogActionCreator.dismissConfirmationDialog();
    }

    private _onStoreChanged = (): void => {
        this.setState(this._getStateFromStores(this.state.showFilterBar));
    }

    private _getStateFromStores(showFilterBar: boolean): IPoolsHubState {
        const nameFilterValue: string | undefined = this._poolFilter.getFilterItemValue(FilterKeys.Name);
        // We are using a single selection dropdown for the filterbar, but get value still returns an array of strings.
        // We need to get the single item from the array if it exists.
        const typeFilterValue: string[] | undefined = this._poolFilter.getFilterItemValue<string[]>(FilterKeys.PoolType);

        return {
            agentQueues: this._getFilteredAgentQueues(this.props.storesHub.agentQueueStore.state, nameFilterValue, typeFilterValue),
            agentPools: this._getFilteredAgentPools(this.props.storesHub.agentPoolStore.state, nameFilterValue, typeFilterValue),
            agentPoolStatuses: this.props.storesHub.agentPoolStatusStore.state,
            filterApplied: !!nameFilterValue || !!typeFilterValue,
            notifications: this.props.storesHub.notificationStore.state,
            showFilterBar
        };
    }

    private _getFilteredAgentQueues(agentQueues: TaskAgentQueue[], nameFilterValue: string | undefined, typeFilterValue: string[] | undefined): TaskAgentQueue[] {
        if (this._projectDataService.hasProjectData() && (!!nameFilterValue || !!typeFilterValue)) {
            return agentQueues.filter((queue: TaskAgentQueue) => queue && queue.pool && this._poolMatchesFilter(queue.name, queue.pool, nameFilterValue, typeFilterValue));
        }
        else {
            return agentQueues;
        }
    }

    private _getFilteredAgentPools(agentPools: TaskAgentPool[], nameFilterValue: string | undefined, typeFilterValue: string[] | undefined): TaskAgentPool[] {
        if (!this._projectDataService.hasProjectData() && (!!nameFilterValue || !!typeFilterValue)) {
            return agentPools.filter((pool: TaskAgentPool) => pool && this._poolMatchesFilter(pool.name, pool, nameFilterValue, typeFilterValue));
        }
        else {
            return agentPools;
        }
    }

    private _poolMatchesFilter(name: string, poolRef: TaskAgentPoolReference, nameFilterValue: string | undefined, typeFilterValue: string[] | undefined): boolean {
        let includePool: boolean = true;

        if (includePool && !!nameFilterValue) {
            includePool = caseInsensitiveContains(name, nameFilterValue);
        }

        if (includePool && !!typeFilterValue && typeFilterValue.length) {
            includePool = typeFilterValue.some( (type: string) => {
                    switch (type) {
                        case Resources.ElasticPoolType:
                            return isElastic(poolRef);
                        case Resources.HostedPoolType:
                            return poolRef.isHosted;
                        case Resources.PrivatePoolType:
                            return !poolRef.isHosted && !isElastic(poolRef);
                        default:
                            return false
                    }
            })
        }

        return includePool;
    }
}
